import { useEffect, useCallback, ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface DetailSheetProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  statusBadge?: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function DetailSheet({
  open,
  onClose,
  title,
  subtitle,
  statusBadge,
  children,
  footer,
  className,
}: DetailSheetProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (open) document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50"
          />

          {/* Sheet */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn(
              'fixed right-0 top-0 z-50 h-full w-[600px] max-w-[90vw]',
              'flex flex-col',
              'border-l border-white/[0.06] bg-[var(--bg-base,var(--bg-base))]',
              'shadow-2xl',
              className,
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-white/[0.06] px-6 py-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-[var(--text-primary,#F0F2F5)] truncate">
                    {title}
                  </h2>
                  {statusBadge}
                </div>
                {subtitle && (
                  <p className="text-xs text-[var(--text-secondary,#94A3B8)] mt-0.5">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className={cn(
                  'rounded-md p-1.5 text-[var(--text-muted)]',
                  'hover:bg-white/[0.06] hover:text-[var(--text-primary)]',
                  'transition-colors',
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="border-t border-white/[0.06] px-6 py-3">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
